/**
 * B2BDashboardPage — Welcome / summary screen for B2B clients
 *
 * Route: /b2b/dashboard  (nested under B2BLayout)
 *
 * Shows:
 *  • Greeting with client name / email
 *  • Quick-action tiles (Go to Catalog, My Orders)
 *  • Cart summary (items currently in cart)
 *  • Phase 4.2 placeholder for order history
 */

import type { ReactNode } from 'react';
import { useAuth }        from '@/features/auth/AuthContext';
import { useB2BCart }     from './B2BCartContext';
import { useB2BUser }     from './B2BUserContext';
import { useB2BTerms }    from './B2BTermsContext';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ClipboardList,
  ShoppingCart,
  Package,
  TrendingUp,
  ChevronLeft,
  Settings,
  ScrollText,
} from 'lucide-react';

// ─── Quick-action tile ────────────────────────────────────────────────────────

interface TileProps {
  icon:    ReactNode;
  label:   string;
  sub?:    string;
  color:   string;
  onClick: () => void;
}

function Tile({ icon, label, sub, color, onClick }: TileProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-4 w-full bg-white rounded-2xl p-4
                 border border-gray-100 shadow-sm hover:shadow-md
                 active:scale-[.98] transition-all text-right"
    >
      <div className={`flex items-center justify-center w-12 h-12 rounded-xl shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5 truncate">{sub}</p>}
      </div>
      <ChevronLeft className="w-4 h-4 text-gray-300 shrink-0" />
    </button>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function B2BDashboardPage() {
  const { user }         = useAuth();
  const navigate         = useNavigate();

  const { items, totalItems, totalPrice } = useB2BCart();
  const { session } = useB2BUser();
  const { terms, showTerms, needsAcceptance } = useB2BTerms();

  const isAgent = session?.role === 'agent';

  // Greeting label — read from the B2B session context (already populated
  // at login), not from router location.state which is always undefined here.
  const displayName =
    session?.activeClientRecord?.name
      ?? session?.clientRecords[0]?.name
      ?? user?.email
      ?? user?.phone
      ?? 'לקוח';

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-6">

      {/* ── Welcome card ─────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-3xl
                      p-6 text-white shadow-lg">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm opacity-80 mb-1">שלום 👋</p>
            <h1 className="text-xl font-bold truncate">{displayName}</h1>
            {isAgent && (
              <span className="mt-2 inline-block text-[11px] font-semibold
                               bg-white/20 px-2.5 py-1 rounded-full">
                סוכן שטח
              </span>
            )}
          </div>
          <button
            onClick={() => navigate('/b2b/settings')}
            className="flex items-center gap-1.5 px-3 py-1.5 mt-1 rounded-xl
                       bg-white/15 hover:bg-white/25 backdrop-blur-sm
                       text-white text-xs font-semibold
                       transition-colors shrink-0"
            aria-label="הגדרות אישיות"
          >
            <Settings className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">הגדרות</span>
          </button>
        </div>
      </div>

      {/* ── Cart summary (when cart has items) ───────────────────────────── */}
      {items.length > 0 && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate('/b2b/catalog')}
          onKeyDown={e => e.key === 'Enter' && navigate('/b2b/catalog')}
          className="flex items-center gap-4 bg-amber-50 border border-amber-200
                     rounded-2xl p-4 cursor-pointer hover:bg-amber-100
                     active:scale-[.98] transition-all"
        >
          <div className="flex items-center justify-center w-12 h-12 rounded-xl
                          bg-amber-100 shrink-0">
            <ShoppingCart className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-amber-900 text-sm">יש לך עגלה פתוחה</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {totalItems} פריטים · ₪{totalPrice.toFixed(2)}
              {(session?.activeClientRecord?.is_vat_liable ?? true) ? ' (לפני מע״מ)' : ''}
            </p>
          </div>
          <ChevronLeft className="w-4 h-4 text-amber-400 shrink-0" />
        </div>
      )}

      {/* ── Quick-action tiles ───────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
          פעולות מהירות
        </h2>

        <Tile
          icon={<BookOpen className="w-6 h-6 text-primary-600" />}
          label="קטלוג מוצרים"
          sub="עיין בכל המוצרים הזמינים"
          color="bg-primary-50"
          onClick={() => navigate('/b2b/catalog')}
        />

        <Tile
          icon={<ClipboardList className="w-6 h-6 text-green-600" />}
          label="ההזמנות שלי"
          sub="צפה בהיסטוריית ההזמנות"
          color="bg-green-50"
          onClick={() => navigate('/b2b/orders')}
        />

        {showTerms && (
          <Tile
            icon={<ScrollText className={`w-6 h-6 ${needsAcceptance ? 'text-amber-600' : 'text-sky-600'}`} />}
            label="התנאים שלי"
            sub={needsAcceptance
              ? 'ממתינים לאישורך'
              : terms?.last_accepted_at
                ? `אושרו ב-${new Date(terms.last_accepted_at).toLocaleDateString('he-IL')} · לצפייה בתנאים`
                : 'תנאי מכירה ומדיניות החזרות'}
            color={needsAcceptance ? 'bg-amber-50' : 'bg-sky-50'}
            onClick={() => navigate('/b2b/terms')}
          />
        )}

        {isAgent && (
          <>
            <Tile
              icon={<Package className="w-6 h-6 text-teal-600" />}
              label="מלאי"
              sub="יתרות מלאי במחסנים שלך"
              color="bg-teal-50"
              onClick={() => navigate('/b2b/inventory')}
            />
            <Tile
              icon={<TrendingUp className="w-6 h-6 text-orange-600" />}
              label="תנועות מלאי"
              sub="כניסות, יציאות והעברות"
              color="bg-orange-50"
              onClick={() => navigate('/b2b/movements')}
            />
          </>
        )}
      </section>

      {/* ── Stats placeholder ────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">
          סיכום
        </h2>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
            <Package className="w-6 h-6 text-gray-300 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-200">—</p>
            <p className="text-xs text-gray-400">הזמנות החודש</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
            <TrendingUp className="w-6 h-6 text-gray-300 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-200">—</p>
            <p className="text-xs text-gray-400">סה״כ השנה</p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 pt-1">
          📊 נתוני הזמנות יוצגו בפייז 4.2
        </p>
      </section>
    </div>
  );
}
